import { useEffect, useRef, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useQuery } from "@tanstack/react-query";
import { nanoid } from "nanoid";
import {
  ChangelogEditor,
  type ChangelogEditorHandle,
} from "@/components/changelog/changelog-editor";
import { useImageUpload } from "@/hooks/use-image-upload";
import {
  handbooksQueryOptions,
} from "@/hooks/use-handbook";
import type { HandbookFormValues } from "@/lib/validation/handbook-schema";
import { HandbookThumbnailField } from "./handbook-thumbnail-field";
import { HandbookParentPicker } from "./handbook-parent-picker";
import { HandbookRelatedPicker } from "./handbook-related-picker";
import { HandbookFootnotesField } from "./handbook-footnotes-field";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ABOUT_SLOT_OPTIONS } from "@/types/handbook";

interface HandbookEditorPanelProps {
  /** 편집 모드일 때 자기 자신 + 자손을 부모 후보에서 제외 */
  currentHandbookId?: string;
}

export function HandbookEditorPanel({
  currentHandbookId,
}: HandbookEditorPanelProps) {
  const form = useFormContext<HandbookFormValues>();
  const editorRef = useRef<ChangelogEditorHandle>(null);
  const { upload: uploadBodyImage } = useImageUpload({
    category: "handbook-body",
  });

  /**
   * 새 footnote 발급 + footnotes 배열 push. id 반환 — bubble menu 의 ✱ 버튼이나
   * footnotes field 의 "+ 각주 추가" 버튼이 같은 함수를 호출하고, 본문 마커 인서트는
   * 호출자가 처리 (bubble menu 는 selection 위치, footnotes field 는 editorRef 통해 cursor).
   */
  const pushNewFootnote = () => {
    const id = nanoid(12);
    const current = form.getValues("footnotes") ?? [];
    form.setValue("footnotes", [...current, { id, content: "" }], {
      shouldValidate: true,
    });
    return id;
  };

  const handleAddFootnoteFromField = () => {
    const id = pushNewFootnote();
    editorRef.current?.insertFootnoteMarker(id);
  };

  // 부모 선택용으로 전체 항목 한 번에 받기 (100개 규모 가정).
  const allHandbooks = useQuery(
    handbooksQueryOptions({}, { page: 1, limit: 200 }),
  );

  const handleBodyImageUpload = async (file: File): Promise<string> => {
    const url = await uploadBodyImage(file);
    if (!url) throw new Error("이미지 업로드에 실패했습니다");
    return url;
  };

  const slug = form.watch("slug");
  const title = form.watch("title");
  const subtitle = form.watch("subtitle") ?? "";
  const thumbnailUrl = form.watch("thumbnailUrl") ?? "";
  const parentIds = form.watch("parentIds") ?? [];
  const relatedIds = form.watch("relatedIds") ?? [];
  const aboutSlot = form.watch("aboutSlot") ?? null;
  const excerpt = form.watch("excerpt") ?? "";
  const tags = form.watch("tags") ?? [];
  const content = form.watch("content");

  /**
   * 슬롯 문서는 위계/관계가 의미 없다 — /about/{슬롯} 정적 라우트가 유일한 입구.
   * slug 도 슬롯이 라우팅을 결정하므로 변경할 이유가 없다 (백엔드는 슬러그 유지/무시).
   * UX: 슬롯 선택 시 slug / parentIds / relatedIds 입력 비활성. submit 단에서도
   * 안전망으로 빈 배열로 강제 (Edit/New page 양쪽).
   */
  const isSlotDoc = aboutSlot !== null;

  /**
   * 태그 입력은 별도 local state 로 — form 값에 직접 split 하면 사용자가 "a, " 까지
   * 친 순간 trailing 빈 토큰이 filter(Boolean) 으로 제거되어 다음 글자를 못 친다.
   * raw 문자열을 그대로 유지하고 blur / Enter 시점에만 commit.
   * form 의 tags (외부 update — reset 등) 가 바뀌면 다시 sync.
   */
  const [tagsText, setTagsText] = useState(tags.join(", "));
  useEffect(() => {
    setTagsText(tags.join(", "));
    // tags 배열을 join key 로 — content of items 가 같으면 무시
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tags.join(" ")]);

  const commitTags = (raw: string) => {
    const next = Array.from(
      new Set(
        raw
          .split(",")
          .map((t) => t.trim())
          .filter(Boolean),
      ),
    ).slice(0, 20);
    form.setValue("tags", next, { shouldValidate: true });
    setTagsText(next.join(", "));
  };

  return (
    <div className="h-full overflow-auto bg-white dark:bg-white">
      <div className="mx-auto max-w-[900px] space-y-4 py-8 pl-14 pr-6">
        {/* Slug + Parent */}
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <div className="space-y-1">
            <label className="text-xs font-medium text-muted-foreground">
              슬러그 (URL 경로)
            </label>
            <input
              type="text"
              value={slug}
              onChange={(e) =>
                form.setValue("slug", e.target.value.toLowerCase(), {
                  shouldValidate: true,
                })
              }
              placeholder="getting-started"
              disabled={isSlotDoc}
              className="w-full rounded-md border border-input bg-background px-3 py-1.5 font-mono text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-1 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-60"
              aria-invalid={Boolean(form.formState.errors.slug)}
            />
            {form.formState.errors.slug && (
              <p className="text-xs text-destructive">
                {form.formState.errors.slug.message}
              </p>
            )}
          </div>

          <HandbookParentPicker
            value={parentIds}
            onChange={(ids) =>
              form.setValue("parentIds", ids, { shouldValidate: true })
            }
            items={allHandbooks.data?.items ?? []}
            excludeId={currentHandbookId}
            disabled={allHandbooks.isLoading || isSlotDoc}
          />
        </div>

        <HandbookRelatedPicker
          value={relatedIds}
          onChange={(ids) =>
            form.setValue("relatedIds", ids, { shouldValidate: true })
          }
          items={allHandbooks.data?.items ?? []}
          excludeId={currentHandbookId}
          disabled={allHandbooks.isLoading || isSlotDoc}
        />

        {isSlotDoc && (
          <p className="text-[11px] text-muted-foreground">
            소개 슬롯에 할당된 문서는 슬러그·상위 항목·관련 문서를 사용하지
            않습니다 — <code>/about/{aboutSlot}</code> 라우트가 단독 입구입니다.
            슬롯을 비우면 다시 편집할 수 있습니다.
          </p>
        )}

        {/* /about/* 슬롯 — 비어두면 일반 doc. 슬롯당 doc 1개 (백엔드 sparse unique). */}
        <div className="space-y-1">
          <label className="text-xs font-medium text-muted-foreground">
            소개 슬롯 (선택, 비어있으면 일반 자료)
          </label>
          <Select
            value={aboutSlot ?? "__none__"}
            onValueChange={(v) =>
              form.setValue(
                "aboutSlot",
                v === "__none__" ? null : (v as "intro" | "korean-occult"),
                { shouldValidate: true },
              )
            }
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder="-" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">— (일반 자료)</SelectItem>
              {ABOUT_SLOT_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-[11px] text-muted-foreground">
            슬롯을 고르면 위키의 <code>/about/{aboutSlot ?? "{슬롯}"}</code> 페이지
            본문이 이 문서로 채워집니다. 같은 슬롯은 한 문서만 가질 수 있어요.
          </p>
        </div>

        <div className="space-y-3">
          <div className="space-y-1">
            <label className="text-xs font-medium text-muted-foreground">
              태그 (쉼표로 구분)
            </label>
            <input
              type="text"
              value={tagsText}
              onChange={(e) => setTagsText(e.target.value)}
              onBlur={(e) => commitTags(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  commitTags(e.currentTarget.value);
                }
              }}
              placeholder="가이드, 사용법, 입문"
              className="w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-1 focus:ring-ring"
            />
          </div>

          <div className="space-y-1">
            <label className="text-xs font-medium text-muted-foreground">
              요약 (선택)
            </label>
            <textarea
              value={excerpt}
              onChange={(e) =>
                form.setValue("excerpt", e.target.value, {
                  shouldValidate: true,
                })
              }
              placeholder="비워두면 본문에서 자동 생성"
              rows={2}
              className="w-full resize-none rounded-md border border-input bg-background px-3 py-1.5 text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-1 focus:ring-ring"
            />
          </div>
        </div>

        {/* Thumbnail */}
        <HandbookThumbnailField
          value={thumbnailUrl}
          onChange={(url) =>
            form.setValue("thumbnailUrl", url, { shouldValidate: true })
          }
          error={form.formState.errors.thumbnailUrl?.message}
        />

        {/* Title (large) */}
        <input
          type="text"
          value={title}
          onChange={(e) =>
            form.setValue("title", e.target.value, { shouldValidate: true })
          }
          placeholder="제목 없는 자료집"
          className="w-full border-none bg-transparent text-4xl font-bold text-foreground placeholder:text-muted-foreground/50 focus:outline-none"
          aria-invalid={Boolean(form.formState.errors.title)}
        />
        {form.formState.errors.title && (
          <p className="text-xs text-destructive">
            {form.formState.errors.title.message}
          </p>
        )}

        {/* Subtitle */}
        <input
          type="text"
          value={subtitle}
          onChange={(e) =>
            form.setValue("subtitle", e.target.value, {
              shouldValidate: true,
            })
          }
          placeholder="부제 (선택, 최대 200자)"
          className="w-full border-none bg-transparent text-lg text-muted-foreground placeholder:text-muted-foreground/50 focus:outline-none"
          aria-invalid={Boolean(form.formState.errors.subtitle)}
        />
        {form.formState.errors.subtitle && (
          <p className="text-xs text-destructive">
            {form.formState.errors.subtitle.message}
          </p>
        )}

        {/* Tiptap editor */}
        <div className="border-t pt-4">
          <ChangelogEditor
            ref={editorRef}
            content={content}
            onChange={(html) =>
              form.setValue("content", html, { shouldValidate: true })
            }
            placeholder="/ 를 입력하여 옵션을 탐색하세요"
            onImageUpload={handleBodyImageUpload}
            enableFootnotes
            onInsertFootnote={pushNewFootnote}
          />
        </div>
        {form.formState.errors.content && (
          <p className="text-xs text-destructive">
            {form.formState.errors.content.message}
          </p>
        )}

        {/* Footnotes 편집 영역 — 본문 마커가 가리키는 콘텐츠 입력 */}
        <HandbookFootnotesField onAddFootnote={handleAddFootnoteFromField} />
      </div>
    </div>
  );
}
